import { PolicyTransactionItem, ITermGroup } from '../types';
import { TERM_STARTING_TRANSACTION_TYPES } from './constants';

/**
 * Groups policy transactions by term.
 * NewBusiness and Renewal transactions start new terms.
 * Other transactions belong to the most recent term before them.
 * Returns groups with newest terms first, and newest transactions first within each term.
 */
export const groupTransactionsByTerm = (transactions: PolicyTransactionItem[]): ITermGroup[] => {
  if (!transactions || transactions.length === 0) {
    return [];
  }

  // Sort transactions by effectiveOn ascending (oldest first) for correct grouping
  const sorted = [...transactions].sort((a, b) => {
    const dateA = new Date(a.effectiveOn).getTime();
    const dateB = new Date(b.effectiveOn).getTime();
    if (dateA !== dateB) return dateA - dateB;
    // If same date, sort by createdAt
    const createdA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const createdB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return createdA - createdB;
  });

  const groups: ITermGroup[] = [];
  let currentGroup: ITermGroup | null = null;

  for (const tx of sorted) {
    const isTermStarter = (TERM_STARTING_TRANSACTION_TYPES as string[]).includes(tx.transactionType);

    if (isTermStarter) {
      // Start a new term - get term number from policyTerm.termCount or derive from count
      const termCount = tx.policyTerm?.termCount;
      const termNumber =
        termCount !== undefined ? String(termCount).padStart(2, '0') : String(groups.length).padStart(2, '0');
      currentGroup = {
        termNumber,
        transactions: [tx],
      };
      groups.push(currentGroup);
    } else if (currentGroup) {
      // Add to current term
      currentGroup.transactions.push(tx);
    } else {
      // Edge case: transaction before any term starter (shouldn't happen normally)
      // Create a default term group
      currentGroup = {
        termNumber: '00',
        transactions: [tx],
      };
      groups.push(currentGroup);
    }
  }

  // Reverse groups so newest terms appear first
  // Reverse transactions within each group so newest appear first
  return groups.reverse().map((group) => ({
    ...group,
    transactions: [...group.transactions].reverse(),
  }));
};

/**
 * Check if there are multiple terms in the grouped transactions
 */
export const hasMultipleTerms = (termGroups: ITermGroup[]): boolean => {
  return termGroups.length > 1 || termGroups.some((group) => Number(group.termNumber) >= 1);
};
